import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Container, Form, Button, Alert, Spinner, Card } from 'react-bootstrap';
import axios from 'axios';
import { API_CONFIG } from '../../config/env';
import { FIAT_CURRENCIES } from '../../constants/fiatCurrencies';
import { exchangeRateService } from '../../services/exchangeRateService';

interface GenerateResponse {
  transactionId: number;
  paymentLinks: string[];
  uniqueWalletAddress: string;
}

const IframePaymentPage: React.FC = () => {
  const { apiKey } = useParams<{ apiKey: string }>();
  const location = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const customFontColor = searchParams.get('fontcolor');
  const customBackgroundColor = searchParams.get('backgroundcolor');
  const userId = searchParams.get('userid');

  // Defaults matching the image: light gray background, dark text, purple button
  const backgroundColor = customBackgroundColor || '#f5f5f5';
  const fontColor = customFontColor || '#1a1a1a';
  const buttonColor = '#8b5cf6'; // Purple button color as shown in image

  const [fiatCurrency, setFiatCurrency] = useState('USD');
  const [fiatAmount, setFiatAmount] = useState('');
  const [currencySearchTerm, setCurrencySearchTerm] = useState('USD');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<GenerateResponse | null>(null);
  const [minimumAmount, setMinimumAmount] = useState<number>(0.5);
  const [minimumAmountLoading, setMinimumAmountLoading] = useState<boolean>(false);

  const currencyDropdownRef = useRef<HTMLDivElement>(null);

  const filteredCurrencies = currencySearchTerm.trim() === ''
    ? FIAT_CURRENCIES
    : FIAT_CURRENCIES.filter(currency =>
        currency.toLowerCase().includes(currencySearchTerm.toLowerCase())
      );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(event.target as Node)) {
        setShowCurrencyDropdown(false);
      }
    };

    if (showCurrencyDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCurrencyDropdown]);

  // Fetch minimum amount when currency changes
  useEffect(() => {
    const fetchMinimumAmount = async () => {
      setMinimumAmountLoading(true);
      try {
        const min = await exchangeRateService.getMinimumAmount(fiatCurrency);
        setMinimumAmount(min);
      } catch (error) {
        console.error('Error fetching minimum amount:', error);
        setMinimumAmount(0.5); // Fallback to USD minimum
      } finally {
        setMinimumAmountLoading(false);
      }
    };

    if (fiatCurrency) {
      fetchMinimumAmount();
    }
  }, [fiatCurrency]);

  const handleCurrencySelect = async (currency: string) => {
    setFiatCurrency(currency);
    setCurrencySearchTerm(currency);
    setShowCurrencyDropdown(false);
    
    // Update minimum amount for new currency
    try {
      const min = await exchangeRateService.getMinimumAmount(currency);
      setMinimumAmount(min);
    } catch (error) {
      console.error('Error fetching minimum amount:', error);
      setMinimumAmount(0.5); // Fallback to USD minimum
    }
  };

  const handleCurrencySearchChange = (value: string) => {
    setCurrencySearchTerm(value);
    setShowCurrencyDropdown(true);
  };

  const handleCurrencyInputFocus = () => {
    setShowCurrencyDropdown(true);
    setCurrencySearchTerm('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccessData(null);

    if (!apiKey) {
      setError('Missing API key');
      return;
    }

    if (!userId) {
      setError('Missing user ID parameter');
      return;
    }

    const amount = parseFloat(fiatAmount);
    if (!fiatAmount || isNaN(amount) || amount < minimumAmount) {
      const minFormatted = exchangeRateService.formatMinimum(fiatCurrency, minimumAmount);
      setError(`Please enter a valid amount (minimum ${minFormatted})`);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        userId: parseInt(userId, 10),
        fiatCurrency,
        amount: parseFloat(fiatAmount),
      };

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-COR-ApiKey': apiKey,
      };

      const token = localStorage.getItem('accessToken');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.post<GenerateResponse>(
        `${API_CONFIG.BASE_URL}/payment-links/generate`,
        payload,
        { headers }
      );

      setSuccessData(response.data);
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const responseMessage = err.response?.data;
        if (typeof responseMessage === 'string') {
          setError(responseMessage);
        } else if (responseMessage?.message) {
          setError(responseMessage.message);
        } else {
          setError('Something went wrong, try again later');
        }
      } else {
        setError('Something went wrong, try again later');
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate a lighter/darker version of background for form elements
  const getFormBackgroundColor = () => {
    if (!customBackgroundColor) return '#ffffff'; // Default: white form on gray background
    // If background is dark, use white for form. If light, use a slightly lighter version
    try {
      const hex = customBackgroundColor.replace('#', '');
      if (hex.length !== 6) return '#ffffff';
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      if (isNaN(r) || isNaN(g) || isNaN(b)) return '#ffffff';
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness < 128 ? '#ffffff' : '#f8f9fa';
    } catch {
      return '#ffffff';
    }
  };

  const formBackgroundColor = getFormBackgroundColor();
  const isDarkBackground = formBackgroundColor === '#ffffff';

  const formControlStyles = {
    backgroundColor: formBackgroundColor,
    color: fontColor,
    border: `1px solid ${isDarkBackground ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.15)'}`,
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '16px',
    transition: 'all 0.2s ease',
  };

  const dropdownStyles = {
    backgroundColor: formBackgroundColor,
    border: `1px solid ${isDarkBackground ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.15)'}`,
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  };

  return (
    <div style={{ backgroundColor, minHeight: '100vh', padding: '2rem 0' }}>
      <Container style={{ maxWidth: '600px' }}>
        <Card style={{ 
          backgroundColor: formBackgroundColor, 
          border: 'none',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
          <Card.Body style={{ padding: '2rem' }}>
            <h3 style={{ color: fontColor, marginBottom: '1.5rem', textAlign: 'center', fontWeight: '600' }}>
              Make Payment
            </h3>

            {error && (
              <Alert variant="danger" style={{ 
                backgroundColor: '#fee', 
                border: '1px solid #fcc', 
                color: '#c33',
                borderRadius: '8px',
              }}>
                {error}
              </Alert>
            )}

            {successData && (
              <Alert variant="success" style={{ 
                backgroundColor: '#efe', 
                border: '1px solid #cfc', 
                color: '#3c3',
                borderRadius: '8px',
              }}>
                Payment links generated successfully.
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-4">
                <Form.Label style={{ color: fontColor, fontWeight: '500', marginBottom: '8px', fontSize: '14px' }}>
                  Fiat Currency
                </Form.Label>
                <div ref={currencyDropdownRef} style={{ position: 'relative' }}>
                  <Form.Control
                    type="text"
                    value={currencySearchTerm}
                    onChange={(e) => handleCurrencySearchChange(e.target.value)}
                    onFocus={handleCurrencyInputFocus}
                    placeholder="Search currency..."
                    style={{
                      ...formControlStyles,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = isDarkBackground ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.25)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = isDarkBackground ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.15)';
                    }}
                  />
                  {showCurrencyDropdown && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        ...dropdownStyles,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                      }}
                    >
                      {filteredCurrencies.length > 0 ? (
                        filteredCurrencies.map((currency, index) => (
                          <div
                            key={currency}
                            onClick={() => handleCurrencySelect(currency)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              color: fontColor,
                              borderBottom: index < filteredCurrencies.length - 1 ? `1px solid ${isDarkBackground ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.08)'}` : 'none',
                              transition: 'background-color 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = isDarkBackground ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.08)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            {currency}
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            padding: '12px 16px',
                            color: fontColor,
                            opacity: 0.6,
                            fontStyle: 'italic',
                          }}
                        >
                          No currencies match your search
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={{ color: fontColor, fontWeight: '500', marginBottom: '8px', fontSize: '14px' }}>
                  Fiat Amount
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={minimumAmount}
                  value={fiatAmount}
                  onChange={(e) => setFiatAmount(e.target.value)}
                  placeholder={minimumAmountLoading ? 'Loading...' : `Enter amount (minimum ${exchangeRateService.formatMinimum(fiatCurrency, minimumAmount)})`}
                  style={formControlStyles}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = isDarkBackground ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = isDarkBackground ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.15)';
                  }}
                />
              </Form.Group>

              <Button
                type="submit"
                disabled={loading}
                className="w-100"
                style={{
                  backgroundColor: buttonColor,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '14px',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.opacity = '0.9';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  }
                }}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Generating...
                  </>
                ) : (
                  'Get payment links'
                )}
              </Button>
            </Form>

            {successData && (
              <div style={{ marginTop: '1.5rem' }}>
                <h5 style={{ color: fontColor, fontWeight: '600', marginBottom: '1rem' }}>Payment Links</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {successData.paymentLinks.map((link, index) => (
                    <a
                      key={`${link}-${index}`}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        color: fontColor,
                        wordBreak: 'break-all',
                        textDecoration: 'none',
                        padding: '8px 12px',
                        backgroundColor: isDarkBackground ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.08)',
                        borderRadius: '6px',
                        transition: 'background-color 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = isDarkBackground ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.12)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = isDarkBackground ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.08)';
                      }}
                    >
                      {link}
                    </a>
                  ))}
                </div>
              </div>
            )}

          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default IframePaymentPage;

